import { useEffect, useState } from 'react';

import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import {
  Button,
  FormControl,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import { useSnackbar } from 'notistack';

import { ICreateCompany, IElement, IProps } from './models';
import { FieldRow, FixedLabel } from './style';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Divider from '@mui/material/Divider';
import SearchableMultiSelect from 'lib/components/SearchableMultiSelect';
import AsyncSearchableMultiSelect from 'lib/components/AsyncSearchableMultiSelect';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';

const CompanyForm = ({
  isEdit,
  setOpen,
  handleLogo,
  logoUrl,
  setLogoUrl,
  handleSaveCompanyModal,
  companies,
  sectors,
  consultants,
  instruments,
  onSearchJobTitles,
  currentId,
}: IProps) => {
  const [selectedSectorIds, setSelectedSectorIds] = useState<string[]>([]);
  const [selectedJobTitleItems, setSelectedJobTitleItems] = useState<
    IElement[]
  >([]);
  const [selectedInstrumentIds, setSelectedInstrumentIds] = useState<string[]>(
    [],
  );
  const [selectedConsultantIds, setSelectedConsultantIds] = useState<string[]>(
    [],
  );
  const selectedCompany = companies?.find((item) => item.id === currentId);
  const [apiKey, setApiKey] = useState<string>(
    isEdit ? selectedCompany?.apikey ?? '' : '',
  );
  const { enqueueSnackbar } = useSnackbar();

  const setFormValuesIfEdit = () => {
    if (selectedCompany) {
      setLogoUrl(selectedCompany.logo);
      setSelectedSectorIds(selectedCompany.sectors.map((s) => s.id));
      setSelectedInstrumentIds(selectedCompany.instruments.map((i) => i.id));
      setSelectedConsultantIds(selectedCompany.consultants.map((c) => c.id));

      setSelectedJobTitleItems(
        selectedCompany.jobTitles.map((j) => ({ id: j.id, name: j.name })),
      );
    }
  };

  useEffect(() => {
    if (isEdit) {
      setFormValuesIfEdit();
    }
  }, []);

  const handleJobTitleSearch = async (
    query: string,
    signal?: AbortSignal,
  ): Promise<IElement[]> => {
    return onSearchJobTitles(query, signal);
  };

  const formik = useFormik({
    initialValues: {
      name: isEdit ? `${selectedCompany?.name}` : '',
      active: isEdit ? selectedCompany?.active ?? true : true,
      ssoEnabled: isEdit ? selectedCompany?.ssoEnabled ?? false : false,
      apiKey: apiKey,
      webhook: {
        url: selectedCompany?.webhook?.url ?? '',
        method: selectedCompany?.webhook?.method ?? '',
        apiKey: selectedCompany?.webhook?.apiKey ?? '',
        payloadProp: selectedCompany?.webhook?.payloadProp ?? '',
      },
    },
    onSubmit: async (values) => {
      const dataToSend: ICreateCompany = {
        name: values.name,
        logo: logoUrl,
        sectorsIds: selectedSectorIds,
        jobTitlesIds: selectedJobTitleItems.map((j) => j.id),
        consultantsIds: selectedConsultantIds,
        instrumentsIds: selectedInstrumentIds,
        active: values.active,
        ssoEnabled: values.ssoEnabled,
        apikey: apiKey,
        webhook: {
          url: values.webhook.url,
          method: values.webhook.method,
          apiKey: values.webhook.apiKey,
          payloadProp: values.webhook.payloadProp,
        },
      };

      handleSaveCompanyModal(dataToSend);
    },
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      {!isEdit ? (
        <DialogTitle id="alert-dialog-title">{'NOVA EMPRESA'}</DialogTitle>
      ) : (
        <DialogTitle id="alert-dialog-title">{'EDITAR EMPRESA'}</DialogTitle>
      )}

      <FieldRow>
        <TextField
          required
          name="name"
          label="Nome da empresa"
          value={formik.values.name}
          onChange={formik.handleChange}
          error={formik.touched.name && Boolean(formik.errors.name)}
          helperText={formik.touched.name && formik.errors.name}
        />
      </FieldRow>

      <FieldRow>
        <FixedLabel>Logo (tamanho recomendado 80x80px)</FixedLabel>
        <TextField
          type="file"
          onChange={(e: any) => {
            handleLogo(e);
          }}
        />
      </FieldRow>

      <FieldRow>
        <SearchableMultiSelect
          items={sectors as IElement[]}
          selectedIds={selectedSectorIds}
          onChange={setSelectedSectorIds}
          label="Setores"
          searchPlaceholder="Buscar setor..."
          emptyMessage="Nenhum setor encontrado"
          singularLabel="setor"
          pluralLabel="setores"
        />
      </FieldRow>

      <FieldRow>
        <SearchableMultiSelect
          items={consultants as IElement[]}
          selectedIds={selectedConsultantIds}
          onChange={setSelectedConsultantIds}
          label="Consultores"
          searchPlaceholder="Buscar consultor..."
          emptyMessage="Nenhum consultor encontrado"
          singularLabel="consultor"
          pluralLabel="consultores"
        />
      </FieldRow>

      <FieldRow>
        <AsyncSearchableMultiSelect
          selectedIds={selectedJobTitleItems.map((j) => j.id)}
          selectedItems={selectedJobTitleItems}
          onChange={setSelectedJobTitleItems}
          onSearch={handleJobTitleSearch}
          label="Cargos"
          searchPlaceholder="Buscar cargo..."
          emptyMessage="Nenhum cargo encontrado"
          singularLabel="cargo"
          pluralLabel="cargos"
        />
      </FieldRow>

      <FieldRow>
        <SearchableMultiSelect
          items={instruments as IElement[]}
          selectedIds={selectedInstrumentIds}
          onChange={setSelectedInstrumentIds}
          label="Instrumentos"
          searchPlaceholder="Buscar instrumento..."
          emptyMessage="Nenhum instrumento encontrado"
          singularLabel="instrumento"
          pluralLabel="instrumentos"
        />
      </FieldRow>

      {/* Seção separada - API Pública */}
      <Divider style={{ margin: '32px 0 16px' }} />

      <FieldRow>
        <FormControl fullWidth>
          <FormControlLabel
            control={
              <Switch
                checked={formik.values.active}
                onChange={(e: any) =>
                  formik.setFieldValue('active', e.target.checked)
                }
                color="primary"
              />
            }
            label="Empresa Ativa"
          />
        </FormControl>
      </FieldRow>

      <FieldRow>
        <FormControl fullWidth>
          <FormControlLabel
            control={
              <Switch
                checked={formik.values.ssoEnabled}
                onChange={(e: any) =>
                  formik.setFieldValue('ssoEnabled', e.target.checked)
                }
                color="primary"
              />
            }
            label="SSO ativo"
          />
          <Typography
            variant="caption"
            color="text.secondary"
            style={{ marginTop: 4 }}
          >
            {formik.values.ssoEnabled
              ? 'Com o SSO ativo, os usuários desta empresa só poderão se autenticar via SSO (Microsoft ou Google).'
              : 'Com o SSO inativo, os usuários desta empresa só poderão se autenticar via email e senha.'}
          </Typography>
        </FormControl>
      </FieldRow>

      <FieldRow>
        <FixedLabel>API Key</FixedLabel>
        {apiKey ? (
          <TextField
            fullWidth
            value={apiKey}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <Tooltip title="Copiar API Key">
                  <IconButton
                    onClick={() => {
                      navigator.clipboard.writeText(apiKey);
                      enqueueSnackbar('Chave copiada com sucesso!', {
                        variant: 'success',
                      });
                    }}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ),
            }}
          />
        ) : (
          <Button
            variant="outlined"
            color="primary"
            onClick={() => {
              const newKey = uuidv4();
              setApiKey(newKey);
              formik.setFieldValue('apiKey', newKey);
            }}
          >
            Gerar API Key
          </Button>
        )}
      </FieldRow>

      {/* Seção separada - API Pública */}
      <Divider style={{ margin: '32px 0 16px' }} />
      <Typography variant="h6" gutterBottom>
        API Pública
      </Typography>

      <FieldRow>
        <TextField
          fullWidth
          style={{ width: '100%' }}
          label="URL Webhook"
          value={formik.values.webhook.url}
          onChange={(e) => formik.setFieldValue('webhook.url', e.target.value)}
        />
      </FieldRow>

      <FieldRow>
        <FormControl fullWidth>
          <InputLabel id="method-label">Método HTTP</InputLabel>
          <Select
            labelId="method-label"
            value={formik.values.webhook.method}
            onChange={(e) =>
              formik.setFieldValue('webhook.method', e.target.value)
            }
          >
            <MenuItem value="POST">POST</MenuItem>
            <MenuItem value="PUT">PUT</MenuItem>
            <MenuItem value="PATCH">PATCH</MenuItem>
          </Select>
        </FormControl>
      </FieldRow>

      <FieldRow>
        <TextField
          fullWidth
          label="API Key (Webhook)"
          value={formik.values.webhook.apiKey}
          onChange={(e) =>
            formik.setFieldValue('webhook.apiKey', e.target.value)
          }
        />
      </FieldRow>

      <DialogActions>
        <Button variant="outlined" color="error" onClick={() => setOpen(false)}>
          Cancelar
        </Button>
        <Button variant="contained" color="success" autoFocus type="submit">
          Salvar
        </Button>
      </DialogActions>
    </form>
  );
};

export { CompanyForm };
